import type { CashierDevice, CashierRef } from '../types';

/** Manager-entered device names (approve, rename, pairing preset): trimmed, 1–64 characters. */
export const DEVICE_NAME_MAX = 64;

/** Move-device reason length accepted by the backend. */
export const MOVE_REASON_MIN = 3;
export const MOVE_REASON_MAX = 500;

export function isValidDeviceName(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length >= 1 && trimmed.length <= DEVICE_NAME_MAX;
}

/** "Counter 1 · 7F3A" — the short code is what the counter screen shows. */
export function deviceOptionLabel(device: Pick<CashierDevice, 'deviceName' | 'shortCode'>): string {
  return device.shortCode ? `${device.deviceName} · ${device.shortCode}` : device.deviceName;
}

/** "Android app · Android 14 · Chrome WebView 129" */
export function deviceDescription(
  device: Pick<CashierDevice, 'deviceType' | 'operatingSystem' | 'browserVersion'>
): string {
  const parts = [device.deviceType, device.operatingSystem, device.browserVersion]
    .map((part) => (part || '').trim())
    .filter(Boolean);
  return parts.length ? parts.join(' · ') : 'Unknown device';
}

/** Name for a device id that is not in the loaded list (short code = last 4 id characters). */
export function fallbackDeviceLabel(deviceId: string): string {
  const compact = deviceId.replace(/-/g, '');
  return `Device ${compact.slice(-4).toUpperCase()}`;
}

export function cashierRefLabel(ref: CashierRef | null | undefined): string {
  if (!ref) return 'Unknown Cashier';
  return (ref.fullName || '').trim() || ref.username || 'Unknown Cashier';
}

/** Elapsed time for "Waiting 3 min". */
export function formatWaitingSince(iso: string | null | undefined, nowMs: number = Date.now()): string {
  if (!iso) return '—';
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return '—';
  const minutes = Math.max(0, Math.floor((nowMs - at) / 60_000));
  if (minutes < 1) return 'less than a minute';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 24) return rest ? `${hours} h ${rest} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  return `${days} d`;
}

/** mm:ss until `expiresAt` (never negative). */
export function formatCountdown(expiresAt: string, nowMs: number = Date.now()): string {
  const target = new Date(expiresAt).getTime();
  const totalSeconds = Number.isNaN(target) ? 0 : Math.max(0, Math.ceil((target - nowMs) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** "deposit" → "Deposit", "cash_drop" → "Cash drop". */
export function attemptedActionLabel(action: string | null | undefined): string {
  const text = (action || '').replace(/[_-]+/g, ' ').trim();
  if (!text) return 'Unknown action';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Shop-side messages for API error codes about cashiers, shifts and devices. */
const SHOP_DEVICE_ERROR_MESSAGES: Record<string, string> = {
  DEVICE_NOT_ACTIVE: 'That device is not approved. Pick another device or choose "Any approved device".',
  DEVICE_NOT_FOUND: 'That device no longer exists. Pick another device or choose "Any approved device".',
  SHIFT_OVERLAP: 'This overlaps another shift for the same Cashier or the pinned device. Pick a different time.',
  CASHIER_HAS_ACTIVE_SHIFT: 'This Cashier has a shift open right now. Close it before assigning a new one.',
  CASHIER_NOT_ACTIVE: 'This Cashier is not active. Reactivate the Cashier or pick another one.',
  CASHIER_NOT_FOUND: 'Cashier not found. Refresh the page and pick the Cashier again.',
  DEVICE_BUSY: 'Another shift is running on that device. Pick an idle device.',
  SAME_DEVICE: 'The shift is already running on that device.',
  INVALID_SHIFT_STATE: 'Only an active shift can be moved. Refresh to see its current status.',
  REASON_REQUIRED: 'Enter a reason (at least 3 characters).',
  DEVICE_REQUIRED: 'Pick the device to move the shift to.',
  DEVICE_NOT_APPROVABLE: 'This device cannot be approved in its current state. Refresh the list.',
  INVALID_DEVICE_NAME: 'Device name must be 1–64 characters.',
  PAIRING_CODE_LIMIT: 'You already have 5 open pairing codes. Use one, or wait until they expire (10 minutes).',
};

/** Friendly message for an API error code, else the server message, else `fallback`. */
export function shopDeviceErrorMessage(
  res: { code?: string | null; message?: string | null } | null | undefined,
  fallback: string
): string {
  const code = res?.code || '';
  if (code && SHOP_DEVICE_ERROR_MESSAGES[code]) return SHOP_DEVICE_ERROR_MESSAGES[code];
  return (res?.message || '').trim() || fallback;
}
